import React, { useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog, DialogContent, DialogActions, Box, Typography, Button, IconButton, Tooltip
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import PrintIcon from '@material-ui/icons/Print';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import CheckIcon from '@material-ui/icons/Check';
import ViewModuleIcon from '@material-ui/icons/ViewModule';

const ratingLabel = (val) => {
  const labels = { 1: 'Needs Improvement', 2: 'Below Expectations', 3: 'Meets Expectations', 4: 'Above Expectations', 5: 'Exceptional' };
  return labels[val] || '';
};

const ratingStars = (val) => '\u2605'.repeat(val || 0) + '\u2606'.repeat(5 - (val || 0));

const getProspectName = (form) => {
  const fullName = `${form?.firstName || ''} ${form?.lastName || ''}`.trim();
  return (form?.refereeName || fullName || '').trim();
};

const formatReport = (form, evaluationCategories, tangibles, selectedTangibles, competencies, selectedCompetencies, selectedEvent) => {
  const date = form.evaluationDate || new Date().toISOString().split('T')[0];
  const prospectName = getProspectName(form);
  const lines = [];
  const allRatings = [];

  lines.push('PROSPECT EVALUATION REPORT');
  lines.push('═'.repeat(50));
  lines.push('');
  lines.push(`Prospect Name:    ${prospectName || 'N/A'}`);
  if (selectedEvent?.eventName) {
    lines.push(`Event:            ${selectedEvent.eventName}`);
  }
  lines.push(`Review Date:      ${date}`);
  const locationParts = [
    selectedEvent?.stateName || selectedEvent?.stateOther || '',
    selectedEvent?.city || '',
  ].filter(Boolean);
  if (locationParts.length > 0) {
    const locationStr = selectedEvent?.city
      ? `${selectedEvent.stateName || selectedEvent.stateOther || ''}, City: ${selectedEvent.city}`
      : locationParts.join(', ');
    lines.push(`State:            ${locationStr}`);
  }
  if (selectedEvent && selectedEvent?.gameType !==0 && selectedEvent?.scoutingCategoryName ==null && selectedEvent?.scoutingSubCategoryName ==null) {
    lines.push(`Game Type:        ${selectedEvent.gameTypeName}`);
  }else{
    lines.push(`Category:       ${selectedEvent?.scoutingCategoryName}`);
    lines.push(`Sub Category:   ${selectedEvent?.scoutingSubCategoryName}`);
  }
  lines.push('');
  lines.push('\u2500'.repeat(50));

  const activeCategories = (evaluationCategories || []).filter(cat => cat.categoryActive);

  activeCategories.forEach(cat => {
    lines.push('');
    lines.push(cat.categoryName.toUpperCase());
    lines.push('\u2500'.repeat(30));

    const activeSubCategories = (cat.subCategories || []).filter(sub => sub.subCategoryActive);

    // Display ratings first
    if (activeSubCategories.length > 0) {
      activeSubCategories.forEach(sub => {
        const rating = form[`sub_${sub.id}`] || 0;
        allRatings.push(rating);
        const padding = ' '.repeat(Math.max(1, 22 - sub.subCategoryName.length));
        lines.push(`  ${sub.subCategoryName}:${padding}${ratingStars(rating)} (${rating}/5 - ${ratingLabel(rating)})`);
        const subKey = `sub_comp_${sub.id}`;
        const subCompNames = (selectedCompetencies[subKey] || [])
          .map(cId => (competencies || []).find(c => c.id === cId))
          .filter(Boolean)
          .map(c => c.competencyName);
        if (subCompNames.length > 0) {
          lines.push(`    Competencies: ${subCompNames.join(', ')}`);
        }
      });
    } else {
      const rating = form[`cat_${cat.id}`] || 0;
      allRatings.push(rating);
      lines.push(`  ${cat.categoryName}:  ${ratingStars(rating)} (${rating}/5 - ${ratingLabel(rating)})`);
      const catKey = `cat_comp_${cat.id}`;
      const catCompNames = (selectedCompetencies[catKey] || [])
        .map(cId => (competencies || []).find(c => c.id === cId))
        .filter(Boolean)
        .map(c => c.competencyName);
      if (catCompNames.length > 0) {
        lines.push(`    Competencies: ${catCompNames.join(', ')}`);
      }
    }

    // Display comments/notes after ratings
    const categoryNotes = form[`category_${cat.id}`];
    if (categoryNotes) {
      lines.push('');
      lines.push(`Comment: ${categoryNotes}`);
    }
  });

  const avg = allRatings.length > 0
    ? (allRatings.reduce((a, b) => a + b, 0) / allRatings.length).toFixed(2)
    : '0.00';

  lines.push('');
  lines.push('\u2550'.repeat(50));
  lines.push(`OVERALL AVERAGE SCORE: ${avg} / 5.0`);
  lines.push('\u2550'.repeat(50));

  lines.push('');
  lines.push('EVALUATION SUMMARY');
  lines.push('\u2500'.repeat(30));
  lines.push(
    form.evaluationSummary && form.evaluationSummary.trim()
      ? form.evaluationSummary
      : 'No Comments'
  );

  const selectedTangibleNames = (tangibles || [])
    .filter(t => (selectedTangibles || []).includes(t.id))
    .map(t => t.name);
  if (selectedTangibleNames.length > 0) {
    lines.push('');
    lines.push('INTANGIBLES');
    lines.push('\u2500'.repeat(30));
    lines.push(selectedTangibleNames.join(', '));
  }

  lines.push('');
  lines.push('\u2500'.repeat(50));
  lines.push(`Report Date: ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`);

  return lines.join('\n');
};

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    maxWidth: 800,
    width: '100%',
  },
  toolbarRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid #e0e0e0',
    '@media print': { display: 'none' },
  },
  reportBody: {
    padding: theme.spacing(3),
    backgroundColor: '#f6f6f6',
  },
  preText: {
    whiteSpace: 'pre-wrap',
    fontFamily: '"Courier New", Courier, monospace',
    fontSize: '0.82rem',
    lineHeight: 1.7,
    margin: 0,
    backgroundColor: '#ffffff',
    padding: theme.spacing(3),
    border: '1px solid #e0e0e0',
    borderRadius: 6,
  },
}));

const EvaluationTextReport = ({
  open,
  onClose,
  form,
  evaluationCategories,
  competencies,
  selectedCompetencies,
  tangibles,
  selectedTangibles,
  selectedEvent,
  onSwitchToVisual,
}) => {
  const classes = useStyles();
  const [copied, setCopied] = useState(false);
  const contentRef = useRef(null);

  if (!form) return null;

  const reportText = formatReport(
    form, evaluationCategories, tangibles, selectedTangibles, competencies, selectedCompetencies, selectedEvent
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(reportText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = reportText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} classes={{ paper: classes.dialogPaper }} scroll="body" disableBackdropClick>
      {/* Toolbar */}
      <Box className={classes.toolbarRow}>
        <Box display="flex" alignItems="center" gridGap={8}>
          <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
            Evaluation Report (Text)
          </Typography>
          {onSwitchToVisual && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<ViewModuleIcon />}
              onClick={onSwitchToVisual}
              style={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              View Visual Report
            </Button>
          )}
        </Box>
        <Box display="flex" alignItems="center" gridGap={8}>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Report content */}
      <DialogContent className={classes.reportBody}>
        <pre className={classes.preText} ref={contentRef}>
          {reportText}
        </pre>
      </DialogContent>
    </Dialog>
  );
};

export default EvaluationTextReport;
